import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const UNIT_TYPE_OPTIONS = [
  { value: "weight", label: "Ağırlık" },
  { value: "height", label: "Yükseklik" },
  { value: "length", label: "Uzunluk" },
  { value: "piece", label: "Adet / Parça" },
  { value: "volume", label: "Hacim" },
  { value: "area", label: "Alan" },
  { value: "time", label: "Zaman" },
  { value: "other", label: "Diğer" },
];

const typeLabel = (type) =>
  UNIT_TYPE_OPTIONS.find((o) => o.value === type)?.label || type || "—";

const tableColumns = [
  {
    name: "Ad",
    dataType: "text",
    id: 1,
    customSorter: (a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || "")),
  },
  {
    name: "Birim",
    dataType: "text",
    id: 2,
    customSorter: (a, b) =>
      String(a?.symbol || "").localeCompare(String(b?.symbol || "")),
  },
  {
    name: "Tür",
    dataType: "text",
    id: 3,
    customSorter: (a, b) =>
      typeLabel(a?.type).localeCompare(typeLabel(b?.type)),
  },
  {
    name: "Durum",
    dataType: "text",
    id: 4,
    customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive),
  },
  { name: "İşlemler", id: 5, sortable: false },
];

const emptyForm = () => ({
  name: "",
  symbol: "",
  type: "piece",
  isActive: true,
});

const PartUnits = () => {
  useTitle("Rovion MES - Parça Birimleri");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/parts",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("part-units-list", tableColumns);

  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchUnits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-units`,
        authHeaders(),
      );
      setUnits(data.units || []);
    } catch {
      addToast("Birimler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const openCreate = () => {
    setFormData(emptyForm());
    setEditingItem(null);
    setSidebarOpened(true);
  };

  const openEdit = (item) => {
    setFormData({
      name: item.name || "",
      symbol: item.symbol || "",
      type: item.type || "piece",
      isActive: item.isActive !== false,
    });
    setEditingItem(item);
    setSidebarOpened(true);
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setEditingItem(null);
    setFormData(emptyForm());
  };

  const handleSave = async () => {
    if (saving) return;
    if (!formData.name.trim()) {
      addToast("Ad zorunludur", "danger");
      return;
    }
    if (!formData.symbol.trim()) {
      addToast("Birim zorunludur", "danger");
      return;
    }
    if (!formData.type) {
      addToast("Tür seçin", "danger");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        symbol: formData.symbol.trim(),
        type: formData.type,
        isActive: formData.isActive,
      };
      if (editingItem) {
        await axios.put(
          `${API_URL}/api/part-units/${editingItem._id}`,
          payload,
          authHeaders(),
        );
        addToast("Güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/part-units`, payload, authHeaders());
        addToast("Oluşturuldu", "success");
      }
      closeSidebar();
      fetchUnits();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/part-units/${id}`, authHeaders());
      addToast("Silindi", "success");
      fetchUnits();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  return (
    <AppPage
      title="Parça Birimleri"
      actions={
        canWrite && (
          <Button
            appearance="primary-animated"
            onClick={openCreate}
            icon={<FiPlus />}
          >
            Yeni Birim
          </Button>
        )
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, id: null })}
        width="small"
      >
        <ModalHeader>Birimi Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            Bu birimi silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger"
              onClick={() => handleDelete(confirmModal.id)}
            >
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setConfirmModal({ isOpen: false, id: null })}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          loading={loading}
          search={true}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {units.map((u) => (
            <Tr key={u._id} data={u}>
              {visibleColumns.includes(1) && (
                <Td>
                  <strong style={{ fontSize: "0.875rem" }}>{u.name}</strong>
                </Td>
              )}
              {visibleColumns.includes(2) && (
                <Td>
                  <span style={{ fontSize: "0.875rem" }}>{u.symbol || "—"}</span>
                </Td>
              )}
              {visibleColumns.includes(3) && (
                <Td>
                  <Lozenge appearance="default-subtle" isBold>
                    {typeLabel(u.type)}
                  </Lozenge>
                </Td>
              )}
              {visibleColumns.includes(4) && (
                <Td>
                  {u.isActive ? (
                    <Lozenge appearance="success-subtle" isBold>
                      Aktif
                    </Lozenge>
                  ) : (
                    <Lozenge appearance="default-subtle" isBold>
                      Pasif
                    </Lozenge>
                  )}
                </Td>
              )}
              {visibleColumns.includes(5) && (
                <Td>
                  <div className="d-flex gap-2">
                    {canWrite && (
                      <>
                        <IconButton
                          icon={<FiEdit2 />}
                          label="Düzenle"
                          appearance="subtle"
                          shape="circle"
                          onClick={() => openEdit(u)}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          label="Sil"
                          appearance="subtle"
                          shape="circle"
                          onClick={() =>
                            setConfirmModal({ isOpen: true, id: u._id })
                          }
                        />
                      </>
                    )}
                  </div>
                </Td>
              )}
            </Tr>
          ))}
        </Table>
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingItem ? "Birim Düzenle" : "Yeni Birim"}
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column gap-3" style={{ paddingBottom: 16 }}>
          <div>
            <Label required>Ad</Label>
            <Input
              value={formData.name}
              onChange={(v) => setFormData((s) => ({ ...s, name: v }))}
              placeholder="örn. Kilogram, Metre, Adet"
            />
          </div>
          <div>
            <Label required>Birim</Label>
            <Input
              value={formData.symbol}
              onChange={(v) => setFormData((s) => ({ ...s, symbol: v }))}
              placeholder="örn. kg, m, adet"
            />
          </div>
          <div>
            <Select
              className="mb-0"
              label="Tür"
              unique
              options={UNIT_TYPE_OPTIONS}
              selected={formData.type ? [formData.type] : []}
              onSelected={(values) => {
                const v = values?.[0]?.value;
                if (v) setFormData((s) => ({ ...s, type: v }));
              }}
              placeholder="Tür seçin"
            />
          </div>
          <div>
            <Select
              className="mb-0"
              label="Durum"
              unique
              options={[
                { value: "1", label: "Aktif" },
                { value: "0", label: "Pasif" },
              ]}
              selected={[formData.isActive ? "1" : "0"]}
              onSelected={(values) => {
                const v = values?.[0]?.value === "0" ? false : true;
                setFormData((s) => ({ ...s, isActive: v }));
              }}
              placeholder="Durum seçin"
            />
          </div>
          <div className="pt-1">
            <Button
              appearance="primary-animated"
              onClick={handleSave}
              disabled={saving}
              icon={editingItem ? <FiEdit2 /> : <FiPlus />}
              className="w-100"
            >
              {saving
                ? "Kaydediliyor..."
                : editingItem
                  ? "Güncelle"
                  : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default withAuth(PartUnits);
